import { Box, Typography, useMediaQuery } from "@mui/material";
import { useState, useMemo } from "react";
import TrimesterTabs from "../../../features/trimester-tabs/TrimesterTabs";
import CarePlanAccordion from "../../../features/care-plan-accordion/CarePlanAccordion";
import { Trimester } from "../../../utility/interfaces/carePlanInterfaces";
import { useGetPatientPlanQuery } from "../../patient-dashboard/services/GetPatientPlanAPISlice";
import { useAppSelector } from "../../../store/hooks";
import { useTranslation } from "react-i18next";
import styles from "./MyCarePlanPage.module.scss";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import {
  CARE_PLAN_FILTERS,
  SKY_BLUE,
} from "../../../utility/constants/common-constants";
import DataLoader from "../../../components/custom-data-loader/DataLoader";
import MyCarePlanPageSkeleton from "./MyCarePlanPageSkeleton";

const MyCarePlanPage = () => {
  const {
    patientProfileInfo: {
      patientProfileId,
      preferredLanguage: patientPreferredLang,
    },
  } = useAppSelector((state) => state.patientProfileDetails);

  const {
    data: carePlanData,
    isLoading,
    error,
  } = useGetPatientPlanQuery({
    patientId: patientProfileId,
    patientPreferredLang,
  });
  const { t } = useTranslation();
  const [selectedTrimester, setSelectedTrimester] = useState<string | number>(
    "All"
  );

  // Filter out empty `trimesterDisplayName`
  const filteredCarePlanData = useMemo(() => {
    return (carePlanData?.patientPlansInfo as Trimester[])?.filter(
      (item) => item.trimesterDisplayName !== ""
    );
  }, [carePlanData]);

  const trimmedDataByTab = useMemo(() => {
    if (selectedTrimester === "All") return filteredCarePlanData;
    return filteredCarePlanData.filter(
      (item) => item.trimesterNumber === selectedTrimester
    );
  }, [selectedTrimester, filteredCarePlanData]);

  const isMobile = useMediaQuery("(max-width: 900px)");

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={carePlanData}
      loadingComponent={<MyCarePlanPageSkeleton />}
      emptyComponent={<Typography>No data found.</Typography>}
      errorComponent={
        <Typography color="error">Error loading care plan data.</Typography>
      }
    >
      {() => (
        <BackgroundLayout
          variant={isMobile ? "carePlanPageMobile" : "carePlanPageDesktop"}
          height={{ xs: "14rem", md: "29rem" }}
          backgroundColor={SKY_BLUE}
          showFooter={true}
        >
          <Box className={styles.carePlanPageContainer}>
            <Box className={styles.backgroundImage} />
            <Box className={styles.contentWrapper}>
              <Typography variant="h1" mb={{ xs: 5, md: 4 }}>
                {t("carePlanPage.myCarePlan")}
              </Typography>

              <TrimesterTabs
                value={selectedTrimester}
                toggleButtonData={CARE_PLAN_FILTERS}
                onChange={setSelectedTrimester}
              />

              <Box mt={3}>
                <CarePlanAccordion data={trimmedDataByTab} />
              </Box>
            </Box>
          </Box>
        </BackgroundLayout>
      )}
    </DataLoader>
  );
};

export default MyCarePlanPage;
